if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          form: "form",
          inputId: "[name=id]",
          submitButton: '[name="add"]',
          errorWrapper: ".f-product-form__error-message-wrapper",
          customFields: ["[data-product-custom-field]"],
        };

        this.domNodes = queryDomNodes(this.selectors, this);

        this.form = this.domNodes.form;
        this.submitButton = this.domNodes.submitButton;
        this.domNodes.inputId.disabled = false;
        this.Notification = window.FoxTheme.Notification;
        this.notificationType = this.dataset.notificationType;
        this.customFields = document.querySelectorAll(
          this.selectors.customFields
        );
        this.cartDrawer = document.querySelector("cart-drawer");
        this.mainCart = document.querySelector("cart-items");
        this.type = this.dataset.type;

        this.variantIdInput = this.domNodes.inputId;

        this.errorWrapper =
          this.type === "product-card"
            ? this.closest(".product-card")
            : this.domNodes.errorWrapper;

        this.form &&
          this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
      }

      validateForm(form) {
        const missingFields = [];
        if (!form) return missingFields;
        const fieldSelectors =
          '[data-product-custom-field] [name][required]:not([hidden]):not([type="hidden"])';
        const requiredFields = form.querySelectorAll(fieldSelectors);
        requiredFields.forEach((field) => {
          field.classList.remove("form-control--warning");
          if (field.type === "radio") {
            const buttons = form.querySelectorAll(
              `input[name="${field.name}"]`
            );
            const selected = Array.from(buttons).some((btn) => btn.checked);
            if (!selected) {
              missingFields.push(field);
              field.classList.add("form-control--warning");
            }
          } else if (!field.value) {
            missingFields.push(field);
            field.classList.add("form-control--warning");
          }
        });
        return missingFields;
      }

      /**
       * The element that keeps this form's `[name="id"]` input in sync. Both of
       * them update the input asynchronously, after a section render request.
       */
      get variantOwner() {
        const productInfo = this.closest("product-info");
        if (productInfo) return productInfo;

        return this.closest("sticky-atc-bar")
          ? document.querySelector("product-info[id^='MainProduct']")
          : null;
      }

      /**
       * A variant change kicks off a fetch and only writes the new variant id
       * into the form once it resolves. Submitting before that would post the
       * previously selected variant, so wait it out first.
       */
      async waitForVariantUpdate() {
        const owner = this.variantOwner;
        if (typeof owner?.whenVariantSettled !== "function") return;
        await owner.whenVariantSettled();
      }

      getSectionsToBundle() {
        let sectionToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", {
            bubbles: true,
            detail: { sections: sectionToBundle },
          })
        );

        if (this.cartDrawer) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.cartDrawer
              .getSectionsToRender()
              .map((section) => section.id),
          ];
        }

        if (this.mainCart) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.mainCart
              .getSectionsToRender()
              .map((section) => section.section),
          ];
        }

        return sectionToBundle;
      }

      /**
       * Shopify answers an over-quantity add with an error payload even when it
       * partially added the available stock ("Only 3 items were added to your
       * cart due to availability."). The error branch has no sections to render,
       * so pull the current cart and its sections to bring the count, the drawer
       * and the cart page back in sync.
       */
      async refreshCartAfterPartialAdd(previousState) {
        const sectionToBundle = this.getSectionsToBundle();

        try {
          const state = await FoxThemeCartHelpers.getState();

          if (!state) return;
          // Nothing made it into the cart, so there is nothing to re-render.
          if (previousState && state.item_count === previousState.item_count) {
            return;
          }

          FoxThemeCartHelpers.updateCartCount(state.item_count);

          let sections = {};
          if (sectionToBundle.length > 0) {
            const response = await fetch(
              `${window.location.pathname}?sections=${sectionToBundle.join(
                ","
              )}`
            );
            sections = await response.json();
          }

          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, {
            ...state,
            sections,
          });
        } catch (e) {
          console.error(e);
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const missing = this.validateForm(
          this.form.closest(".f-product-single__blocks")
        );

        if (missing && missing.length > 0) {
          return this.Notification.show({
            target: this.domNodes.errorWrapper,
            method: "appendChild",
            type: "warning",
            message: window.FoxThemeStrings.requiredField,
          });
        }

        if (this.submitButton.classList.contains("btn--loading")) return;

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("btn--loading");

        const config = {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };

        await this.waitForVariantUpdate();

        // Switching to another product in a combined listing swaps the whole
        // product-info (or <main>), so this form no longer represents what is
        // on screen. The replaced node is only removed after a delay, hence
        // the data-replaced check next to isConnected.
        if (!this.isConnected || this.closest("[data-replaced]")) {
          this.submitButton.classList.remove("btn--loading");
          this.submitButton.removeAttribute("aria-disabled");
          return;
        }

        const formData = new FormData(this.form);

        const sectionToBundle = this.getSectionsToBundle();

        formData.append("sections", sectionToBundle);

        if (this.mainCart || this.cartDrawer) {
          formData.append("sections_url", window.location.pathname);
        }

        config.body = formData;
        const { FoxThemeSettings, FoxThemeStrings } = window;
        // Snapshot the cart before the add so the error branch can tell a
        // partial add (stock did land in the cart) from a rejected one. It has
        // to settle first, otherwise it could race the add and come back with
        // the post-add count.
        const cartStateBeforeAdd = await FoxThemeCartHelpers.getState().catch(
          () => null
        );
        fetch(`${FoxThemeSettings.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.description,
                message: response.message,
              });

              // Dispatch event for third-party
              document.dispatchEvent(
                new CustomEvent("product-ajax:error", {
                  detail: {
                    errorMessage: response.description,
                  },
                })
              );

              this.error = true;

              this.refreshCartAfterPartialAdd(cartStateBeforeAdd);

              return this.Notification.show({
                target:
                  this.notificationType === "toast"
                    ? document.body
                    : this.errorWrapper,
                method: "appendChild",
                type: "warning",
                message: response.description || response.message,
                last: 3000,
                sticky: this.notificationType === "toast",
              });
            }

            if (
              !FoxThemeSettings.cart.openDrawerWhenAdded ||
              FoxThemeSettings.cart.cartType === "page"
            ) {
              this.Notification.show({
                target: this.errorWrapper ? this.errorWrapper : document.body,
                method: "appendChild",
                type: "success",
                message: FoxThemeStrings.item_added,
                last: 3000,
                sticky: this.notificationType === "toast",
              });
            }
            if (FoxThemeSettings.cart.cartType === "page") {
              FoxThemeCartHelpers.getState().then((state) => {
                FoxThemeCartHelpers.updateCartCount(state.item_count);
              });
            }

            this.error = false;
            window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, response);

            // DispatchEvent for third-party
            document.dispatchEvent(
              new CustomEvent("product-ajax:added", {
                detail: {
                  product: response,
                },
              })
            );
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("btn--loading");
            this.submitButton.removeAttribute("aria-disabled");
          });
      }
    }
  );
}
